import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const eventDetails = [
  {
    icon: 'ri-calendar-check-line',
    label: 'Format',
    value: 'Full day immersive experience',
  },
  {
    icon: 'ri-user-heart-line',
    label: 'Who',
    value: 'Women ready to heal, lead, and become',
  },
  {
    icon: 'ri-stack-line',
    label: 'What',
    value: 'Keynotes, panels, workshops, community',
  },
  {
    icon: 'ri-sparkling-line',
    label: 'Next event',
    value: 'Coming 2026',
  },
];

export default function ConferenceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );

      if (cardsRef.current) {
        const cardEls = cardsRef.current.children;
        gsap.fromTo(
          cardEls,
          { opacity: 0, y: 25 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.12,
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 82%',
            },
          }
        );
      }

      gsap.fromTo(
        buttonsRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: buttonsRef.current,
            start: 'top 88%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="women-who-heal"
      ref={sectionRef}
      className="relative overflow-hidden min-h-[600px] lg:min-h-[700px]"
    >
      {/* Full-bleed conference background image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/2d29664032ce3429a58840abfd6f4538/f9db06f345469e06f1198fd736ebb6d7.jpeg"
          alt="African women gathered in sisterhood, sharing wisdom and mutual connection"
          className="w-full h-full object-cover object-center"
          width="1600"
          height="900"
        />
      </div>

      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[600px] lg:min-h-[700px] px-4 md:px-8 lg:px-12 py-20 md:py-28 lg:py-32">
        <div ref={contentRef} className="text-center max-w-3xl">
          {/* Eyebrow */}
          <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6">
            Annual flagship event
          </p>

          {/* Headline */}
          <h2 className="font-heading text-xl md:text-3xl lg:text-5xl leading-[1.1] text-[#FDFAF8] mb-5">
            Women Who Heal.
          </h2>

          {/* Tagline */}
          <p className="font-quote text-base md:text-xl italic text-[#F0B8D0] mb-8">
            The conference for women who are done being quiet about their healing.
          </p>

          {/* Description */}
          <p className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed max-w-2xl mx-auto mb-6">
            This year, for the first time, Mandie is gathering the most extraordinary women in one room, and something is already shifting in the air.
          </p>

          {/* Container bridge */}
          <p className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed max-w-2xl mx-auto mb-3">
            Here is a container for every woman.
          </p>
          <p className="font-quote text-base md:text-xl italic text-[#F0B8D0] mb-12">
            The only question is: which one is calling you?
          </p>
        </div>

        {/* Event Detail Cards */}
        <div
          ref={cardsRef}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 w-full max-w-3xl mb-10"
        >
          {eventDetails.map((detail, index) => (
            <div
              key={index}
              className="bg-[#FDFAF8]/10 backdrop-blur-sm border border-[#C9A84C]/30 rounded-lg px-4 py-5 text-center group hover:bg-[#FDFAF8]/15 hover:border-[#C9A84C]/50 transition-all duration-300"
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3">
                <i className={`${detail.icon} text-[#C9A84C] text-lg`}></i>
              </div>
              <p className="text-[#C9A84C] text-[10px] font-body tracking-[0.2em] uppercase mb-1.5">
                {detail.label}
              </p>
              <p className="font-body text-xs md:text-sm text-[#FDFAF8] leading-snug">
                {detail.value}
              </p>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div ref={buttonsRef} className="flex flex-col items-stretch sm:flex-row sm:items-center justify-center gap-3 sm:gap-4">
          <a
            href="#email-capture"
            className="inline-flex items-center justify-center px-8 py-3.5 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
          >
            Join the Waitlist
          </a>
          <a
            href="/women-who-heal"
            className="inline-flex items-center justify-center px-8 py-3.5 border border-[#C9A84C] text-[#C9A84C] text-sm font-body font-medium rounded-full hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 whitespace-nowrap w-full sm:w-auto"
          >
            Learn More
          </a>
        </div>
      </div>
    </section>
  );
}